// AppErrorBoundary - Prevents white screen of death
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertCircle } from 'lucide-react';
import FirebaseConfigError from './FirebaseConfigError';
import { isChunkLoadError, retryChunkLoadOnce } from '../lib/lazyWithRetry';
import { reportError } from '../lib/monitoring/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) {
      retryChunkLoadOnce();
      return null;
    }
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AppErrorBoundary] Caught error:', error);
    console.error('[AppErrorBoundary] Error info:', errorInfo);
    if (isChunkLoadError(error) && retryChunkLoadOnce()) {
      return;
    }
    reportError(error, { boundary: 'AppErrorBoundary', componentStack: errorInfo.componentStack });
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if this is a Firebase config error
      const firebaseConfigError = typeof window !== 'undefined' && (window as any).__FIREBASE_CONFIG_ERROR__;
      if (firebaseConfigError && firebaseConfigError.missing) {
        return <FirebaseConfigError missing={firebaseConfigError.missing} message={firebaseConfigError.message} />;
      }
      
      // Also check if error message contains Firebase config keywords
      if (this.state.error && (
        this.state.error.message.includes('Firebase Config') ||
        this.state.error.message.includes('environment variables') ||
        this.state.error.message.includes('VITE_FB_')
      )) {
        const missing = this.state.error.message.match(/VITE_FB_\w+/g) || [];
        if (missing.length > 0) {
          return <FirebaseConfigError missing={missing} message={this.state.error.message} />;
        }
      }

      return (
        <div className="min-h-screen bg-[var(--surface-muted)] flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                  Something went wrong
                </h2>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  This page failed to load. Try reloading. If it keeps happening, sign out and
                  sign back in, or contact support.
                </p>
              </div>
              <Button
                onClick={this.handleReload}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                Reload Page
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

